import React, { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import ReactDatePicker from "react-datepicker";
import { InputGroup } from "reactstrap";
import { readStoredLabUser, useLab } from "../../../_helper/PharmacyProvider";
import {
  API_DATE_FORMAT,
  dateFormatHasMonthName,
  formatDateValue,
  formatDisplayDate,
  getDateFormatMaxLength,
  isCalendarToday,
  isDateInputDigitComplete,
  isDateWithinBounds,
  maskDateInput,
  parseBoundaryDate,
  parseDateValue,
  parseTypedDate,
  resolveDatePickerPlaceholder,
  resolvePickerDateFormat,
  toApiDateString,
} from "../../../utils/dateTime/dateFormat";
import "./DatePicker.css";

const DatePickerInput = forwardRef(
  (
    {
      value,
      onClick,
      onChange,
      onBlur,
      onFocus,
      onKeyDown,
      className,
      placeholder,
      disabled,
      readOnly,
      name,
      id,
      autoComplete,
      required,
      maxLength,
      inputMode = "text",
      "aria-label": ariaLabel,
    },
    ref
  ) => (
    <input
      ref={ref}
      id={id}
      name={name}
      type="text"
      className={className}
      value={value ?? ""}
      onClick={onClick}
      onChange={onChange}
      onBlur={onBlur}
      onFocus={onFocus}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      disabled={disabled}
      readOnly={readOnly}
      autoComplete={autoComplete}
      required={required}
      maxLength={maxLength}
      aria-label={ariaLabel}
      inputMode={inputMode}
      autoCapitalize="off"
      autoCorrect="off"
      spellCheck={false}
    />
  )
);

DatePickerInput.displayName = "DatePickerInput";

/** Bootstrap spacing utilities belong on the InputGroup, not the nested input. */
const BOOTSTRAP_SPACING_CLASS_RE =
  /^(m[trblxyse]?|p[trblxyse]?)-([0-5]|auto)$/;

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const splitDatePickerClassName = (className = "") => {
  const wrapper = [];
  const input = [];
  String(className)
    .split(/\s+/)
    .filter(Boolean)
    .forEach((token) => {
      if (BOOTSTRAP_SPACING_CLASS_RE.test(token)) {
        wrapper.push(token);
      } else {
        input.push(token);
      }
    });
  return {
    wrapperSpacingClassName: wrapper.join(" "),
    inputClassName: input.join(" "),
  };
};

/**
 * Reusable Bootstrap-style date picker.
 *
 * Display (UI / keyboard):
 * - Explicit `dateFormat` prop, else the logged-in lab user's `date_format`
 * - Examples: DD/MM/YYYY, MM/DD/YYYY, YYYY-MM-DD, DD-MM-YYYY, DD MMM YYYY
 *
 * Form / API value (`onChange` → `event.target.value`):
 * - Always emits canonical `YYYY-MM-DD` (`API_DATE_FORMAT`)
 * - Controlled `value` prop should also be stored as `YYYY-MM-DD`
 * - Display format is never sent to APIs
 */
const DatePicker = ({
  value = "",
  onChange,
  name,
  id,
  dateFormat,
  placeholder,
  disabled = false,
  readOnly = false,
  minDate,
  maxDate,
  className = "form-control",
  wrapperClassName = "",
  style,
  isClearable = true,
  showIcon = true,
  autoComplete = "off",
  required = false,
  showMonthDropdown = true,
  showYearDropdown = true,
  dropdownMode = "select",
  peekNextMonth = true,
  yearDropdownItemNumber = 100,
  scrollableYearDropdown = true,
  ...rest
}) => {
  const labContext = useLab() || {};
  const labUser = labContext.currentLab || readStoredLabUser();
  const datePickerRef = useRef(null);
  const { wrapperSpacingClassName, inputClassName } =
    splitDatePickerClassName(className);
  const resolvedInputClassName = inputClassName || "form-control";
  // Priority: explicit prop → logged-in user date_format → default
  const resolvedDateFormat = resolvePickerDateFormat(dateFormat, labUser);
  const allowsMonthName = dateFormatHasMonthName(resolvedDateFormat);
  // Prefer the resolved format guide; ignore field-label placeholders like "From Date".
  const resolvedPlaceholder = resolveDatePickerPlaceholder(
    placeholder,
    resolvedDateFormat
  );
  const inputMaxLength = getDateFormatMaxLength(resolvedDateFormat);
  const iconDisabled = disabled || readOnly;

  // Parent/form state is expected in API format (YYYY-MM-DD).
  // Fall back to display-format parse only for resilience.
  const selected = useMemo(
    () => parseDateValue(value, [API_DATE_FORMAT, resolvedDateFormat]),
    [value, resolvedDateFormat]
  );

  const resolvedMinDate = useMemo(() => parseBoundaryDate(minDate), [minDate]);
  const resolvedMaxDate = useMemo(() => parseBoundaryDate(maxDate), [maxDate]);

  const calendarYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const minYear = resolvedMinDate
      ? resolvedMinDate.getFullYear()
      : currentYear - (yearDropdownItemNumber || 100);
    const maxYear = resolvedMaxDate
      ? resolvedMaxDate.getFullYear()
      : currentYear + 20;

    const start = Math.min(minYear, maxYear);
    const end = Math.max(minYear, maxYear);

    const list = [];
    for (let y = start; y <= end; y += 1) {
      list.push(y);
    }
    return list;
  }, [resolvedMinDate, resolvedMaxDate, yearDropdownItemNumber]);

  const [isEditing, setIsEditing] = useState(false);
  const [draftText, setDraftText] = useState("");

  useEffect(() => {
    if (isEditing) return;
    setDraftText(selected ? formatDisplayDate(selected, resolvedDateFormat) : "");
  }, [selected, resolvedDateFormat, isEditing]);

  /** Always notify parent with API date string (YYYY-MM-DD), never display format. */
  const emitChange = (date) => {
    if (!onChange) return;
    const apiValue = date ? formatDateValue(date) : "";
    onChange(
      {
        target: {
          name: name || "",
          value: apiValue,
        },
      },
      date || null,
      apiValue
    );
  };

  /** Keep the open calendar month/year aligned with a committed date immediately. */
  const syncCalendarViewToDate = (date) => {
    const picker = datePickerRef.current;
    if (!picker || !(date instanceof Date)) return;

    if (typeof picker.setPreSelection === "function") {
      picker.setPreSelection(date);
    }
    if (typeof picker.changeMonthYear === "function") {
      picker.changeMonthYear(date);
    } else {
      if (typeof picker.changeYear === "function") {
        picker.changeYear(date.getFullYear());
      }
      if (typeof picker.changeMonth === "function") {
        picker.changeMonth(date.getMonth());
      }
    }
  };

  const tryCommitText = (text, { commitEmpty = true, requireCompleteDigits = false } = {}) => {
    const trimmed = String(text ?? "").trim();
    if (!trimmed) {
      if (commitEmpty) {
        emitChange(null);
        setDraftText("");
      }
      return true;
    }

    // While typing, wait for a full digit mask (e.g. do not commit 1996-11-2 as Nov 2).
    // Blur/Enter still allow flexible unpadded dates via parseTypedDate.
    if (
      requireCompleteDigits &&
      !isDateInputDigitComplete(trimmed, resolvedDateFormat)
    ) {
      return false;
    }

    const parsed = parseTypedDate(trimmed, resolvedDateFormat);
    if (
      !parsed ||
      !isDateWithinBounds(parsed, resolvedMinDate, resolvedMaxDate)
    ) {
      return false;
    }

    setDraftText(formatDisplayDate(parsed, resolvedDateFormat));
    emitChange(parsed);
    syncCalendarViewToDate(parsed);
    return true;
  };

  const handleSelect = (date) => {
    setIsEditing(false);
    if (!date) {
      emitChange(null);
      setDraftText("");
      return;
    }
    if (!isDateWithinBounds(date, resolvedMinDate, resolvedMaxDate)) return;
    setDraftText(formatDisplayDate(date, resolvedDateFormat));
    emitChange(date);
    syncCalendarViewToDate(date);
  };

  const handleChangeRaw = (event) => {
    // react-datepicker also invokes onChangeRaw for calendar day clicks (before
    // onChange). Those are not text edits — ignore them so we do not clear the
    // value. Critical when re-selecting the same day: onChange is skipped.
    const target = event?.target;
    const isTextInputEvent =
      target &&
      typeof target.value === "string" &&
      (typeof target.tagName !== "string" ||
        target.tagName.toLowerCase() === "input");
    if (!isTextInputEvent) {
      if (typeof event?.preventDefault === "function") {
        event.preventDefault();
      }
      return;
    }

    event.preventDefault();
    if (disabled || readOnly) return;

    const nextText = maskDateInput(
      target.value,
      resolvedDateFormat,
      draftText
    );
    setIsEditing(true);
    setDraftText(nextText);

    if (!nextText.trim()) {
      emitChange(null);
      return;
    }

    tryCommitText(nextText, {
      commitEmpty: false,
      requireCompleteDigits: true,
    });
  };

  const handleBlur = () => {
    const committed = tryCommitText(draftText, { commitEmpty: true });
    if (!committed) {
      setDraftText(selected ? formatDisplayDate(selected, resolvedDateFormat) : "");
    }
    setIsEditing(false);
  };

  const handleFocus = () => {
    if (disabled || readOnly) return;
    setIsEditing(true);
    setDraftText(
      selected ? formatDisplayDate(selected, resolvedDateFormat) : draftText
    );
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      const committed = tryCommitText(draftText, { commitEmpty: true });
      if (!committed) {
        setDraftText(
          selected ? formatDisplayDate(selected, resolvedDateFormat) : ""
        );
      }
      setIsEditing(false);
      event.target?.blur?.();
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      setDraftText(
        selected ? formatDisplayDate(selected, resolvedDateFormat) : ""
      );
      setIsEditing(false);
      event.target?.blur?.();
      return;
    }

    // Month-name formats (MMM): keep A–Z / space in the input.
    // Stop bubbling so an open calendar/month dropdown cannot swallow letter keys.
    if (
      allowsMonthName &&
      (event.key === " " || /^[a-zA-Z]$/.test(event.key))
    ) {
      event.stopPropagation();
    }
  };

  const openCalendarFromIcon = (event) => {
    event.preventDefault();
    if (iconDisabled) return;
    const picker = datePickerRef.current;
    if (!picker) return;
    // Focus the input, then open the calendar popup.
    if (typeof picker.setFocus === "function") {
      picker.setFocus();
    }
    if (typeof picker.setOpen === "function") {
      picker.setOpen(true);
    }
  };

  const renderCustomHeader = ({
    date,
    changeYear,
    changeMonth,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }) => {
    const headerYear = date.getFullYear();
    const headerMonth = date.getMonth();

    let yearOptions = calendarYears;
    if (yearOptions.length > 0 && !yearOptions.includes(headerYear)) {
      const min = Math.min(yearOptions[0], headerYear);
      const max = Math.max(yearOptions[yearOptions.length - 1], headerYear);
      const extendedYears = [];
      for (let y = min; y <= max; y += 1) {
        extendedYears.push(y);
      }
      yearOptions = extendedYears;
    }

    const isMonthOptionDisabled = (monthIndex) => {
      if (resolvedMinDate) {
        const minYear = resolvedMinDate.getFullYear();
        const minMonth = resolvedMinDate.getMonth();
        if (headerYear < minYear || (headerYear === minYear && monthIndex < minMonth)) {
          return true;
        }
      }
      if (resolvedMaxDate) {
        const maxYear = resolvedMaxDate.getFullYear();
        const maxMonth = resolvedMaxDate.getMonth();
        if (headerYear > maxYear || (headerYear === maxYear && monthIndex > maxMonth)) {
          return true;
        }
      }
      return false;
    };

    const handleYearChange = (e) => {
      const newYear = Number(e.target.value);
      changeYear(newYear);
      if (
        resolvedMinDate &&
        newYear === resolvedMinDate.getFullYear() &&
        headerMonth < resolvedMinDate.getMonth()
      ) {
        changeMonth(resolvedMinDate.getMonth());
      } else if (
        resolvedMaxDate &&
        newYear === resolvedMaxDate.getFullYear() &&
        headerMonth > resolvedMaxDate.getMonth()
      ) {
        changeMonth(resolvedMaxDate.getMonth());
      }
    };

    return (
      <div className="pharmacy-panel-datepicker-custom-header">
        <button
          type="button"
          className="pharmacy-panel-datepicker-nav-btn pharmacy-panel-datepicker-nav-btn--prev"
          onClick={decreaseMonth}
          disabled={prevMonthButtonDisabled}
          aria-label="Previous Month"
        >
          <i className="fa fa-chevron-left" aria-hidden="true" />
        </button>
        <div className="pharmacy-panel-datepicker-header-controls">
          {showMonthDropdown ? (
            <select
              className="pharmacy-panel-datepicker-header-select pharmacy-panel-datepicker-header-select--month"
              value={headerMonth}
              onChange={(e) => changeMonth(Number(e.target.value))}
              onMouseDown={(e) => e.stopPropagation()}
              aria-label="Select month"
            >
              {MONTH_NAMES.map((monthName, index) => (
                <option
                  key={monthName}
                  value={index}
                  disabled={isMonthOptionDisabled(index)}
                >
                  {monthName}
                </option>
              ))}
            </select>
          ) : (
            <span className="pharmacy-panel-datepicker-header-label">
              {MONTH_NAMES[headerMonth]}
            </span>
          )}
          {showYearDropdown ? (
            <select
              className="pharmacy-panel-datepicker-header-select pharmacy-panel-datepicker-header-select--year"
              value={headerYear}
              onChange={handleYearChange}
              onMouseDown={(e) => e.stopPropagation()}
              aria-label="Select year"
            >
              {yearOptions.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          ) : (
            <span className="pharmacy-panel-datepicker-header-label">
              {headerYear}
            </span>
          )}
        </div>
        <button
          type="button"
          className="pharmacy-panel-datepicker-nav-btn pharmacy-panel-datepicker-nav-btn--next"
          onClick={increaseMonth}
          disabled={nextMonthButtonDisabled}
          aria-label="Next Month"
        >
          <i className="fa fa-chevron-right" aria-hidden="true" />
        </button>
      </div>
    );
  };

  const inputValue = isEditing
    ? draftText
    : selected
      ? formatDisplayDate(selected, resolvedDateFormat)
      : draftText;

  const calendarIconLabel = `Open calendar${
    resolvedPlaceholder ? ` (${resolvedPlaceholder})` : ""
  }`;

  return (
    <InputGroup
      className={`date pharmacy-panel-datepicker flex-nowrap ${
        showIcon ? "has-calendar-icon" : ""
      } ${
        !disabled && !readOnly && isClearable ? "is-clearable" : ""
      } ${wrapperSpacingClassName} ${wrapperClassName}`.trim()}
      style={style}
    >
      <ReactDatePicker
        ref={datePickerRef}
        id={id || name}
        name={name}
        selected={selected}
        openToDate={selected || undefined}
        onChange={handleSelect}
        onChangeRaw={handleChangeRaw}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        value={inputValue}
        dateFormat={resolvedDateFormat}
        placeholderText={resolvedPlaceholder}
        disabled={disabled}
        readOnly={readOnly}
        minDate={resolvedMinDate || undefined}
        maxDate={resolvedMaxDate || undefined}
        isClearable={!disabled && !readOnly && isClearable}
        className={resolvedInputClassName}
        autoComplete={autoComplete}
        required={required}
        showPopperArrow={false}
        popperPlacement="bottom-start"
        popperClassName="pharmacy-panel-datepicker-popper"
        calendarClassName="pharmacy-panel-datepicker-calendar"
        portalId="pharmacy-panel-datepicker-portal"
        popperProps={{ strategy: "fixed" }}
        renderCustomHeader={renderCustomHeader}
        peekNextMonth={peekNextMonth}
        showMonthDropdown={showMonthDropdown}
        showYearDropdown={showYearDropdown}
        dropdownMode={dropdownMode}
        yearDropdownItemNumber={yearDropdownItemNumber}
        scrollableYearDropdown={scrollableYearDropdown}
        strictParsing
        dayClassName={(date) =>
          isCalendarToday(date) ? "pharmacy-panel-datepicker__day--is-today" : undefined
        }
        customInput={
          <DatePickerInput
            className={resolvedInputClassName}
            placeholder={resolvedPlaceholder}
            disabled={disabled}
            readOnly={readOnly}
            name={name}
            id={id || name}
            autoComplete={autoComplete}
            required={required}
            maxLength={inputMaxLength}
            aria-label={resolvedPlaceholder}
            inputMode={allowsMonthName ? "text" : "numeric"}
          />
        }
        {...rest}
      />
      {showIcon ? (
        <button
          type="button"
          className="input-group-text pharmacy-panel-datepicker-icon-btn"
          onClick={openCalendarFromIcon}
          onMouseDown={(event) => {
            // Keep focus path predictable; avoid stealing focus before setFocus.
            if (!iconDisabled) event.preventDefault();
          }}
          disabled={iconDisabled}
          tabIndex={iconDisabled ? -1 : 0}
          aria-label={calendarIconLabel}
          aria-disabled={iconDisabled}
          title={calendarIconLabel}
        >
          <i className="fa fa-calendar" aria-hidden="true" />
        </button>
      ) : null}
    </InputGroup>
  );
};

export { API_DATE_FORMAT, toApiDateString };
export default DatePicker;
